import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import Features from "@/pages/Features";
import Developers from "@/pages/Developers";
import Docs from "@/pages/Docs";
import Security from "@/pages/Security";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Checkout from "@/pages/Checkout";
import LinkRedirect from "@/pages/LinkRedirect";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Payments from "@/pages/dashboard/Payments";
import PaymentLinks from "@/pages/dashboard/PaymentLinks";
import CustomCheckout from "@/pages/dashboard/CustomCheckout";
import WalletPage from "@/pages/dashboard/WalletPage";
import Payout from "@/pages/dashboard/Payout";
import Transactions from "@/pages/dashboard/Transactions";
import ApiKeys from "@/pages/dashboard/ApiKeys";
import Webhooks from "@/pages/dashboard/Webhooks";
import Developers2 from "@/pages/dashboard/DevelopersPanel";
import Settings from "@/pages/dashboard/Settings";
import Support from "@/pages/dashboard/Support";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminCommissions from "@/pages/admin/AdminCommissions";
import AdminCommissionWallets from "@/pages/admin/AdminCommissionWallets";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminWebhooks from "@/pages/admin/AdminWebhooks";
import AdminSettings from "@/pages/admin/AdminSettings";

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <Loader2 className="animate-spin text-primary" size={32} />
    </div>
  );
}

function Protected({ children, role }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (role === "merchant" && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/security" element={<Security />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/refund-policy" element={<LegalPage slug="refund" />} />
            <Route path="/aml-policy" element={<LegalPage slug="aml" />} />
            <Route path="/risk-disclosure" element={<LegalPage slug="risk" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/adc" element={<Login admin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pay/:paymentId" element={<Checkout />} />
            <Route path="/l/:linkId" element={<LinkRedirect />} />

            <Route path="/dashboard" element={<Protected role="merchant"><DashboardLayout /></Protected>}>
              <Route index element={<Overview />} />
              <Route path="payments" element={<Payments />} />
              <Route path="payment-links" element={<PaymentLinks />} />
              <Route path="checkout" element={<CustomCheckout />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="payout" element={<Payout />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="api" element={<ApiKeys />} />
              <Route path="webhooks" element={<Webhooks />} />
              <Route path="developers" element={<Developers2 />} />
              <Route path="settings" element={<Settings />} />
              <Route path="support" element={<Support />} />
            </Route>

            <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="commission-wallets" element={<AdminCommissionWallets />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="webhooks" element={<AdminWebhooks />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
